// src/components/heygen/HeyGenTemplatesPage.tsx
// HeyGen template gallery — pick template, fill variables, generate video.

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Play, Video } from 'lucide-react';

import {
  ApiError, listTemplates, generateFromTemplate, checkJob,
  type HeyGenTemplate, type HeyGenJob,
} from '@/lib/api_heygen';
import { useJobPoller } from '@/hooks/useJobPoller';
import { VideoPlayer } from '@/components/shared/VideoPlayer';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { ApiKeyMissing } from '@/components/shared/ApiKeyMissing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function HeyGenTemplatesPage({ addNotification }: { addNotification?: Notifier }) {
  const [templates, setTemplates] = useState<HeyGenTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiMissing, setApiMissing] = useState(false);

  const [selected, setSelected] = useState<HeyGenTemplate | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setApiMissing(false);
    try {
      const list = await listTemplates();
      setTemplates(list);
    } catch (e: any) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 502)) setApiMissing(true);
      else addNotification?.({ type: 'error', title: 'Error cargando templates', message: e.message });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (selected?.variables) {
      const init: Record<string, string> = {};
      for (const v of Object.keys(selected.variables)) init[v] = '';
      setVariables(init);
    }
  }, [selected]);

  const poller = useJobPoller<HeyGenJob>({
    checkFn: checkJob,
    jobId,
    interval: 4000,
    onComplete: (r: HeyGenJob) => {
      setVideoUrl(r.video_url || null);
      addNotification?.({ type: 'success', title: 'Video generado desde template' });
    },
    onFail: (r: HeyGenJob) => {
      addNotification?.({ type: 'error', title: 'Error', message: r.error_message || 'Failed' });
    },
  });

  const handleGenerate = async () => {
    if (!selected) return;
    setSubmitting(true);
    setVideoUrl(null);
    setJobId(null);
    try {
      const job = await generateFromTemplate({
        template_id: selected.template_id,
        variables,
      });
      setJobId(job.id);
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error', message: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (apiMissing) return <ApiKeyMissing service="HeyGen" />;
  if (loading) return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-white/40" /></div>;

  const isPolling = poller.status === 'polling';

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Template grid */}
      <div>
        <label className="text-xs text-white/50 mb-2 block">Elige un template</label>
        {templates.length === 0 ? (
          <p className="text-sm text-white/40">Sin templates disponibles.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-72 overflow-y-auto pr-1">
            {templates.map((t) => (
              <button
                key={t.template_id}
                onClick={() => setSelected(t)}
                className={`rounded-lg overflow-hidden border-2 text-left transition-all ${
                  selected?.template_id === t.template_id
                    ? 'border-[var(--acc1)] shadow-lg shadow-[var(--acc1)]/20'
                    : 'border-transparent hover:border-white/20'
                }`}
              >
                {t.thumbnail_url ? (
                  <img src={t.thumbnail_url} alt={t.name} className="w-full h-24 object-cover" />
                ) : (
                  <div className="w-full h-24 bg-white/10 flex items-center justify-center">
                    <Video size={20} className="text-white/20" />
                  </div>
                )}
                <p className="text-[10px] text-white/60 px-2 py-1 truncate">{t.name}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Variables form */}
      {selected && selected.variables && Object.keys(selected.variables).length > 0 && (
        <div className="o-card border border-white/10 space-y-3">
          <p className="text-xs text-white/50 font-medium">Variables del template</p>
          {Object.keys(selected.variables).map((varName: string) => (
            <div key={varName}>
              <label className="text-xs text-white/40 mb-0.5 block">{varName}</label>
              <input
                value={variables[varName] || ''}
                onChange={(e) => setVariables((p) => ({ ...p, [varName]: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)]"
                placeholder={`Valor para ${varName}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Generate */}
      {selected && (
        <button
          onClick={handleGenerate}
          disabled={submitting || isPolling}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
        >
          {submitting || isPolling ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
          {isPolling ? 'Generando…' : 'Generar desde template'}
        </button>
      )}

      {/* Status */}
      {jobId && poller.status !== 'idle' && (
        <div className="o-card flex items-center gap-3">
          <JobStatusBadge status={poller.data?.status || 'pending'} />
          <span className="text-sm text-white/60">
            {poller.status === 'polling' && 'Procesando…'}
            {poller.status === 'completed' && 'Video listo'}
            {poller.status === 'failed' && (poller.error || 'Error')}
          </span>
        </div>
      )}

      {videoUrl && (
        <div className="o-card border border-emerald-500/30">
          <VideoPlayer src={videoUrl} />
        </div>
      )}
    </div>
  );
}
